/* eslint-disable no-param-reassign */

// Thousands separators and the spacing characters that show up around them in
// values pasted by users or resolved from a CRM: regular space, NBSP, narrow
// NBSP (all covered by \s) plus the comma and apostrophe groupings.
const GROUPING_CHARACTERS = /[\s,']/g;

// A counter value can reach this element as a formatted string — either from a
// dynamic variable resolved on the live link ("17,646") or from a story that
// persisted `number` as text. Parse those the same way the element renders
// them, treating the comma as a thousands separator.
export const parseNumericValue = (value: unknown): number => {
  if (typeof value === "number") return value;
  if (typeof value !== "string") return Number.NaN;
  return Number.parseFloat(value.replace(GROUPING_CHARACTERS, ""));
};

export const formatNumberWithCommas = (
  number: number,
  decimal: boolean,
): string => {
  if (decimal) return String(number);
  const parts = number.toString().split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return parts.join(".");
};

export const setRkpiInnerHtml = (
  element: Element,
  number: number,
  opts: { decimal?: boolean; prefix?: string | null; suffix?: string | null },
) => {
  const { prefix, suffix, decimal = false } = opts;

  element.innerHTML = formatNumberWithCommas(number, decimal).toString();
  if (prefix) element.innerHTML = prefix + element.innerHTML;
  if (suffix) element.innerHTML = element.innerHTML + suffix;
};

export const animateRkpi = (
  element: Element,
  start: number,
  end: number,
  duration: number,
  delay = 0,
  prefix: string | null = null,
  suffix: string | null = null,
  decimal = false,
) => {
  const digitsCount: number = countDigits(end);

  const stopper = {
    stop: false,
  };

  let timeout: ReturnType<typeof setTimeout> | null = setTimeout(() => {
    let startTimestamp: number | null = null;
    const step = (timestamp: number) => {
      if (stopper.stop) return;

      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      setRkpiInnerHtml(
        element,
        Number((progress * (end - start) + start).toFixed(digitsCount)),
        {
          decimal,
          prefix,
          suffix,
        },
      );

      if (progress < 1) {
        window.requestAnimationFrame(step);
      }
    };
    window.requestAnimationFrame(step);
    timeout = null;
  }, delay);

  return {
    stop: () => {
      if (timeout != null) clearTimeout(timeout);
      stopper.stop = true;
    },
  };
};

export const countDigits = (number: number): number => {
  const numberString = number.toString();
  if (numberString.indexOf(".") !== -1) {
    const decimalPart = numberString.split(".")[1];
    return decimalPart.length;
  }
  return 0;
};

export const getSpeed = (speed: string): number => {
  switch (speed) {
    case "slow":
      return 4;
    case "medium":
      return 2;
    case "fast":
      return 0.5;
    default:
      return 2;
  }
};
